import * as React from 'react'

import { ContentType, apiRequest } from '../services/api'

const API_ENDPOINT = '/api/newsletter/widget'

const MESSAGES = {
  empty: 'Please provide an email address',
  success: 'Successfully subscribed to the mailing list',
  confirm: 'A confirmation email has been sent to the provided address',
  error: 'An error occured subscribing to the list, please try again later',
}

const DEFAULT_STATE = { isLoading: false, isError: false, isConfirm: false }

type HookState = typeof DEFAULT_STATE

/**
 * Values of GdprConsentLog
 */
export enum Source {
  Blog = 10,
}

export function useMailingListSubmit(source: Source, callback?: (state: HookState) => void) {
  const [message, setMessage] = React.useState<string | null>(null)
  const [state, setState] = React.useState(DEFAULT_STATE)

  const handleSubmit = React.useRef(async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault()

    const email = ev.currentTarget.email as HTMLInputElement | null

    if (!email || email?.value.trim().length === 0) {
      setMessage(MESSAGES.empty)
      setState({ ...DEFAULT_STATE, isError: true })
      return
    }

    let response: Response | null = null
    let newState: Partial<HookState> = {}
    let newMessage: string | null = null

    setState({ ...DEFAULT_STATE, isLoading: true })
    try {
      response = await apiRequest(
        API_ENDPOINT,
        {
          headers: { 'Content-Type': ContentType.FormUrlEncoded },
          method: 'POST',
          body: new URLSearchParams({ email: email.value.trim(), source: source.toString() }),
          keepalive: true,
        },
        true,
        // Return raw response object & skip parsing, we want to check the URL
        true
      )

      const resUrl = new URL(response.url)

      // Error sending confirmation email
      if (resUrl.pathname === API_ENDPOINT) {
        const message = await response.text()
        newState = { ...DEFAULT_STATE, isError: true }
        newMessage = message
        return
      }

      // User needs to confirm message
      if (resUrl.searchParams.get('confirm')) {
        newState = { ...DEFAULT_STATE, isConfirm: true }
        newMessage = MESSAGES.confirm
        return
      }

      newMessage = MESSAGES.success
    } catch {
      newState = { ...DEFAULT_STATE, isError: true }
      newMessage = MESSAGES.error
    } finally {
      const finalState = { ...DEFAULT_STATE, ...newState }
      setMessage(newMessage)
      setState(finalState)
      callback?.(finalState)
    }
  })

  return { state, message, handleSubmit: handleSubmit.current }
}
